import React from "react";

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    const date = new Date(value);
    const datePart = new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(date);
    const timePart = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: true })
      .format(date).replace(":", ".").replace(/\s+/g, " ").trim();
    return `${datePart} ${timePart}`;
  } catch { return String(value); }
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  try { return new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value)); }
  catch { return String(value); }
};

const toText = (value, fallback = "-") => { const t = String(value ?? "").trim(); return t || fallback; };

export const MY_TASK_HISTORY_PDF_COLUMNS = ["title", "description", "ticket", "assignee", "action", "priority", "status", "due"];

// Up to this many columns the table still reads comfortably on a portrait A4 page.
const PORTRAIT_COLUMN_LIMIT = 5;

export function MyTaskHistoryPdfDocument({ components, logoUrl, companyInfo, filters, items, selectedColumns, generatedAt }) {
  const { Document, Page, StyleSheet, Text, View, Image } = components;
  const rows = Array.isArray(items) ? items : [];
  const activeColumns = Array.isArray(selectedColumns) && selectedColumns.length
    ? selectedColumns
    : MY_TASK_HISTORY_PDF_COLUMNS;
  const orientation = activeColumns.length > PORTRAIT_COLUMN_LIMIT ? "landscape" : "portrait";

  const styles = StyleSheet.create({
    page: {
      paddingTop: 28,
      paddingBottom: 28,
      paddingHorizontal: 30,
      backgroundColor: "#ffffff",
      fontFamily: "HindSiliguri",
      fontSize: 8.6,
      color: "#0f172a",
    },

    /* ── Header ── */
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingBottom: 10,
      borderBottomWidth: 1.5,
      borderBottomColor: "#0f172a",
      marginBottom: 12,
    },
    brandRow: { flexDirection: "column", alignItems: "flex-start" },
    logo: { width: 100, objectFit: "contain", marginBottom: 2 },
    companyName: { fontSize: 15, fontWeight: 700, color: "#0f172a", letterSpacing: -0.2 },
    companyTagline: { fontSize: 8, color: "#64748b", marginTop: 1, letterSpacing: 0.3 },
    contactBlock: { alignItems: "flex-start" },
    contactRow: { flexDirection: "row", alignItems: "center", marginBottom: 3 },
    contactLabel: { fontSize: 8, fontWeight: 700, color: "#0f172a", width: 52 },
    contactLine: { fontSize: 8, color: "#475569" },

    /* ── Meta bar ── */
    metaBar: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#f8fafc",
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 6,
      paddingVertical: 7,
      paddingHorizontal: 11,
      marginBottom: 14,
    },
    metaLeft: { flexDirection: "column" },
    metaReportName: { fontSize: 11, fontWeight: 700, color: "#0f172a", marginBottom: 1 },
    metaFilter: { fontSize: 7.8, color: "#475569", lineHeight: 1.55 },
    metaRight: { alignItems: "flex-end" },
    metaGenLabel: { fontSize: 7, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 1 },
    metaGenValue: { fontSize: 8, color: "#334155" },

    /* ── Table ── */
    table: {
      borderWidth: 1,
      borderColor: "#e2e8f0",
      borderRadius: 7,
      overflow: "hidden",
    },
    tHead: {
      flexDirection: "row",
      borderBottomWidth: 1.5,
      borderBottomColor: "#0f172a",
      paddingVertical: 7,
      paddingHorizontal: 4,
    },
    tHeadCell: {
      fontSize: 7.2,
      fontWeight: 700,
      color: "#0f172a",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      paddingHorizontal: 6,
    },
    tRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#f1f5f9",
      paddingVertical: 6,
      paddingHorizontal: 4,
    },
    tRowAlt: { backgroundColor: "#f8fafc" },
    tRowLast: { borderBottomWidth: 0 },
    cell: { paddingHorizontal: 6 },
    cellStrong: { fontSize: 7.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.4 },
    cellText: { fontSize: 7.1, color: "#475569", lineHeight: 1.35, marginTop: 1 },

    /* ── Empty / Footer ── */
    emptyBox: {
      borderWidth: 1, borderColor: "#e2e8f0", borderRadius: 7,
      paddingVertical: 20, alignItems: "center",
    },
    emptyText: { fontSize: 9, color: "#94a3b8" },
    footer: {
      marginTop: 10, paddingTop: 8,
      borderTopWidth: 1, borderTopColor: "#e2e8f0",
      flexDirection: "row", justifyContent: "space-between", alignItems: "center",
    },
    footerText: { fontSize: 7.6, color: "#94a3b8" },
  });

  const filterParts = [
    (filters?.startDate || filters?.endDate) ? `Date: ${formatDateOnly(filters?.startDate)} to ${formatDateOnly(filters?.endDate)}` : null,
    filters?.status && filters.status !== "All" ? `Status: ${filters.status}` : null,
    filters?.assigneeName && filters.assigneeName !== "All" ? `Assignee: ${filters.assigneeName}` : null,
    filters?.priority && filters.priority !== "All" ? `Priority: ${filters.priority}` : null,
    filters?.actionType && filters.actionType !== "All" ? `Action: ${filters.actionType}` : null,
    filters?.searchText ? `Search: "${filters.searchText}"` : null,
  ].filter(Boolean);

  const completedCount = rows.filter((item) => String(item.historyStatus || item.taskStatus || "").toLowerCase() === "completed").length;
  const urgentCount = rows.filter((item) => String(item.taskPriority || "").toLowerCase() === "urgent").length;

  const allColumns = [
    {
      key: "title",
      header: "Title",
      weight: 16,
      render: (item) => (
        <Text style={styles.cellStrong}>{toText(item.taskTitle, "Untitled task")}</Text>
      ),
    },
    {
      key: "description",
      header: "Description",
      weight: 20,
      render: (item) => (
        <Text style={styles.cellText}>{toText(item.taskDescription, "No description")}</Text>
      ),
    },
    {
      key: "ticket",
      header: "Ticket",
      weight: 12,
      render: (item) => (
        <>
          <Text style={styles.cellStrong}>{toText(item.ticketNumber, "Manual task")}</Text>
          <Text style={styles.cellText}>{toText(item.ticketSubject, "No subject")}</Text>
        </>
      ),
    },
    {
      key: "assignee",
      header: "Assignee",
      weight: 13,
      render: (item) => (
        <>
          <Text style={styles.cellStrong}>{toText(item.assignedEmployeeName, "Unassigned")}</Text>
          <Text style={styles.cellText}>{toText(item.assignedEmployeeEmail, "No email")}</Text>
        </>
      ),
    },
    {
      key: "action",
      header: "Action",
      weight: 13,
      render: (item) => (
        <>
          <Text style={styles.cellStrong}>{toText(item.actionLabel, "Task Updated")}</Text>
          <Text style={styles.cellText}>by {toText(item.actorName, "Unknown")}</Text>
          <Text style={styles.cellText}>{formatDateTime(item.createdAt)}</Text>
        </>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      weight: 7,
      render: (item) => (
        <Text style={styles.cellStrong}>{toText(item.taskPriority, "Low")}</Text>
      ),
    },
    {
      key: "status",
      header: "Status",
      weight: 10,
      render: (item) => (
        <>
          <Text style={styles.cellStrong}>{toText(item.historyStatus, "Pending")}</Text>
          <Text style={styles.cellText}>Progress {Number(item.historyProgressPercent || 0)}%</Text>
        </>
      ),
    },
    {
      key: "due",
      header: "Due",
      weight: 9,
      render: (item) => (
        <Text style={styles.cellText}>{formatDateOnly(item.taskDueDate)}</Text>
      ),
    },
  ];

  const visibleColumns = allColumns.filter((column) => activeColumns.includes(column.key));
  const totalWeight = visibleColumns.reduce((sum, column) => sum + column.weight, 0) || 1;
  const columns = visibleColumns.map((column) => ({
    ...column,
    width: `${(column.weight / totalWeight) * 100}%`,
  }));

  return (
    <Document title="Task History Report" author="BanglaFire CRM" subject="Task history report" creator="BanglaFire CRM" producer="BanglaFire CRM">
      <Page size="A4" orientation={orientation} style={styles.page}>

        {/* ── HEADER ── */}
        <View style={styles.header}>
          <View style={styles.brandRow}>
            {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
            <Text style={styles.companyName}>{toText(companyInfo?.name, "BanglaFire CRM")}</Text>
            <Text style={styles.companyTagline}>Customer Relationship Management</Text>
          </View>
          <View style={styles.contactBlock}>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Address :</Text>
              <Text style={styles.contactLine}>39/9 A, Senpara Parbata, Mirpur, Dhaka-1216</Text>
            </View>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Mobile :</Text>
              <Text style={styles.contactLine}>(+880) 9639222999  |  1709927934</Text>
            </View>
            <View style={styles.contactRow}>
              <Text style={styles.contactLabel}>Email :</Text>
              <Text style={styles.contactLine}>md@banglafire.com  |  support@banglafire.com</Text>
            </View>
          </View>
        </View>

        {/* ── META BAR ── */}
        <View style={styles.metaBar}>
          <Text style={[styles.metaReportName, { flex: 1, marginBottom: 0, paddingRight: 10 }]}>
            Task History Report
            {filters?.userName ? `   •   User: ${filters.userName}` : ""}
            {filterParts.length > 0 ? `   •   ${filterParts.join("   •   ")}` : ""}
          </Text>
          <Text style={styles.metaGenValue}>Generated: {formatDateTime(generatedAt)}</Text>
        </View>

        {/* ── TABLE ── */}
        {rows.length > 0 ? (
          <View style={styles.table}>
            <View style={styles.tHead}>
              {columns.map((column) => (
                <Text key={column.key} style={[styles.tHeadCell, { width: column.width }]}>
                  {column.header}
                </Text>
              ))}
            </View>
            {rows.map((item, index) => (
              <View
                key={`${item.historyId}-${item.taskId}-${index}`}
                style={[styles.tRow, index % 2 === 1 ? styles.tRowAlt : null, index === rows.length - 1 ? styles.tRowLast : null]}
                wrap={false}
              >
                {columns.map((column) => (
                  <View key={column.key} style={[styles.cell, { width: column.width }]}>
                    {column.render(item)}
                  </View>
                ))}
              </View>
            ))}
          </View>
        ) : (
          <View style={styles.emptyBox}>
            <Text style={styles.emptyText}>No task history matched the selected filters.</Text>
          </View>
        )}

        {/* ── FOOTER ── */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Generated on {formatDateTime(generatedAt)}</Text>
          <Text style={styles.footerText}>BanglaFire CRM  |  Confidential</Text>
        </View>
      </Page>
    </Document>
  );
}
